import { useBoolean, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { paramsReportToSendMapping } from '@helper/budget/store/paramsReportSendMapping'
import { validateGroupName } from '@helper/budget/store/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/budget/store/validateIfShowColumnInTable'
import { parseStoreBudgetData } from '@helper/grid/budget/store'
import { parseStoreHeaderBudgetData } from '@helper/grid/budget/store-header'
import budget from '@service/budget'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IBudgetStoreContext,
  IParamsToSend,
  Pagination,
  IUpdateParams
} from '@base/@types/providers/Budget/store'
import { reportFilter } from '@base/variables/filters/budget/storeFilter'
import { reportRequest } from '@base/variables/filters/budget/storeRequest'
import budgetCommercialHeader from '@base/variables/headers/budget/store'
const StoreBudgetProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const filtersList: ILayoutFilter[] = reportFilter

  const [filename, setFilename] = useState('Orçamento Loja - Relatório')

  const [dataUpdate, updateDataUpdate] = useImmer<any[]>([])
  const [data, updateData] = useImmer<any[]>([])
  const [query, setQuery] = useState('')
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])

  const [params, updateParams] = useImmer<any>(reportRequest)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    budgetCommercialHeader
  )
  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...reportRequest }

    return paramsFormatted
  })

  const clearAllFilters = () => {
    const paramsFormatted = { ...reportRequest }

    updateParams({
      ...params,
      empresaSelectIds: [],
      centroCdLojaIds: true,
      centroCdLojaSelectIds: [],
      categoriasIds: false,
      categoriasSelectIds: [],
      anosSelectIds: [],
      mesesIds: false,
      mesesSelectIds: [],
      historicoSelectIds: [],
      operacaoIds: false,
      operacaoSelectIds: []
    })

    setParamsToSend(paramsFormatted)
    if (localStorage.getItem('budgetStoreParams')) {
      localStorage.setItem(
        'budgetStoreParams',
        JSON.stringify({
          ...params,
          empresaSelectIds: [],
          centroCdLojaIds: true,
          centroCdLojaSelectIds: [],
          categoriasIds: false,
          categoriasSelectIds: [],
          anosSelectIds: [],
          mesesIds: false,
          mesesSelectIds: [],
          historicoSelectIds: [],
          operacaoIds: false,
          operacaoSelectIds: []
        })
      )
    }
    if (localStorage.getItem('budgetStoreParamsRequest')) {
      localStorage.setItem(
        'budgetStoreParamsRequest',
        JSON.stringify(paramsFormatted)
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsReportToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('budgetStoreParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('budgetStoreParams') || ''
      )

      localStorage.setItem(
        'budgetStoreParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('budgetStoreParamsRequest')) {
      localStorage.setItem(
        'budgetStoreParamsRequest',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }
  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('budgetStoreParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetStoreParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('budgetStoreParams', JSON.stringify(params))
    }
    if (localStorage.getItem('budgetStoreParamsRequest')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetStoreParamsRequest') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'budgetStoreParamsRequest',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])
  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateHeaders(newHeaders)
    },
    [headers, updateHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'budgetStoreAnalitc',
            data,
            headers
          },
          {
            type: 'excel',
            entity: 'budgetStoreConsolidated',
            data,
            headers
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [data, headers, toggleColumn])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@AlertPurchaseParamsToSend:',
        JSON.stringify(paramsToSend)
      )

      budget
        .getBudgetReport(paramsToSend)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => updateData([]))
      setLoading(false)
    },
    [paramsToSend, updateData, updatePagination]
  )

  const updateShowTableInTable = useCallback(
    async (data: any[]) => {
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })

      const headersFormatted = validateIfShowColumnInTable(
        headers,
        paramsToSend
      )

      updateHeaders(headersFormatted)
    },
    [updateData, updateHeaders, headers, paramsToSend]
  )
  const getData = useCallback(async () => {
    setLoading(true)
    let newParams = {}
    if (localStorage.getItem('budgetStoreParamsRequest')) {
      newParams = JSON.parse(
        localStorage.getItem('budgetStoreParamsRequest') || ''
      )
    } else {
      newParams = paramsToSend
    }

    localStorage.setItem(
      '@BudgetConciliationParamsToSend:',
      JSON.stringify(newParams)
    )
    budget
      .getBudgetStore(newParams)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePagination(pagination)
        setFilters.toggle()
        setLoading(false)
      })
      .catch(() => {
        setFilters.toggle()
        updateData([])
        setLoading(false)
      })
  }, [
    paramsToSend,
    updateShowTableInTable,
    updatePagination,
    setFilters,
    updateData
  ])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        isMulti: false
      }
    ]
  }, [])

  const exportDataConsolidated = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const obj = {
      orcamentoLojas: data,
      groupByFilterBudget: {
        year: true,
        month: true,
        company: true,
        store: true,
        compactCategory: true,
        category: true,
        categoryManager: true,
        productManager: true,
        historic: true,
        operation: true
      }
    }
    try {
      const { data: res } = await budget.postExportStoreConsolidated(obj)
      fileDownload(res, filename + '-consolidado.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()

      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [data, toast, filename])

  const exportDataAnalitc = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    try {
      const { data: res } = await budget.postExportStoreAnalityc(paramsToSend)
      fileDownload(res, filename + '-analitco.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
      Swal.close()
    }
  }, [paramsToSend, filename, toast])

  const handleUpdateGrowthWanted = useCallback(
    async (index: number, wanted: number | string) => {
      let stores = []

      stores = data
      const store = stores.filter(x => x.id === Number(index))[0]
      const idData = stores.findIndex((x: any) => x.id === Number(index))
      try {
        try {
          stores = [...stores] // important to create a copy, otherwise you'll modify state outside of setState call
          stores[idData] = {
            ...store,
            previsaoAnoAtual: wanted,
            alterado: true
          }
          const newData = stores
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(newData)
          })
          updateDataUpdate([...dataUpdate, idData])
        } catch (e) {}
      } catch {}
    },
    [data, updateData, dataUpdate, updateDataUpdate]
  )
  const saveUpdate = useCallback(async () => {
    setLoading(true)
    const uniqueIndexs = dataUpdate.filter((v, i, a) => a.indexOf(v) === i)
    const datasUpdates: any = []
    let auxCount = 0
    uniqueIndexs.forEach(index => {
      if (auxCount === 0) {
        datasUpdates.push({
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          ano: data[index].ano ? data[index].ano : 0,
          go: 0,
          empresaId: data[index].empresaId ? data[index].empresaId : 0,
          previsaoFinal: data[index].previsaoAnoAtual
            ? data[index].previsaoAnoAtual
            : 0
        })
      } else {
        datasUpdates.push({
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          go: 0,
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          ano: data[index].ano ? data[index].ano : 0,
          empresaId: data[index].empresaId ? data[index].empresaId : 0,
          previsaoFinal: data[index].previsaoAnoAtual
            ? data[index].previsaoAnoAtual
            : 0
        })
      }
      auxCount++
    })
    budget
      .putStoreBudget({ orcamentoLoja: [...datasUpdates] })
      .then(({ data: { _data } }) => {
        setLoading(false)
        toast({
          title: 'Sucesso.',
          description: 'Salvo edição em Loja',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, dataUpdate, toast, data])
  return (
    <StoreBudgetContext.Provider
      value={{
        loading,

        filtersList,
        pagination,
        tableInteraction,
        hiddenColumns,
        data: parseStoreBudgetData(data),
        actions,
        params,
        handlePageChange,

        headers,
        filename,
        dataUpdate,
        handleUpdateGrowthWanted,
        saveUpdate,
        dataFooter: parseStoreHeaderBudgetData(data),
        filters,
        query,
        setFilename,
        exportDataConsolidated,
        exportDataAnalitc,
        setQuery,
        toggleColumn,
        handleUpdateParams,
        handleLocalParams,

        getData,
        clearAllFilters,

        setFilters
      }}
    >
      {children}
    </StoreBudgetContext.Provider>
  )
}

export default StoreBudgetProvider
export const StoreBudgetContext = createContext<IBudgetStoreContext>(
  {} as IBudgetStoreContext
)
